import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useCreatePriceList, usePriceListsOverview, useProducts, useUpdatePriceList } from '../../hooks';
import { summarizePriceLists } from '../../lib/priceListCoverage';
import type { ToastSeverity } from '../../lib/useToast';
import { PRICE_LIST_KIND_LABELS } from '../../model/options';
import type { PriceListRequest, PriceListResponse, PriceListSummary } from '../../model/priceListTypes';
import { PriceListCoveragePanel } from './PriceListCoveragePanel';
import { PriceListFormDialog } from './PriceListFormDialog';

export type PriceListsView = 'lists' | 'coverage';

export interface PriceListsPanelProps {
  view: PriceListsView;
  onViewChange: (view: PriceListsView) => void;
  seedStore: string | null;
  /** `catalog.pricing.manage` — list create/rename + STATIC-line authoring. */
  canManage: boolean;
  /** `settings.pricing.manage` — store bind/unbind + list activate/deactivate (adr-pricing-025 d9). */
  canAdmin: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Price-lists tab — sub-view toggle between the Lists table (§8.1) and the Store-coverage
 * view (§8.4), mirroring `RetailEvotorStoresPanel`'s sub-view toggle. `usePriceListsOverview`
 * composes both lists+bindings+best-effort-lines ONCE so switching sub-views never re-fetches.
 */
export function PriceListsPanel({
  view,
  onViewChange,
  seedStore,
  canManage,
  canAdmin,
  onResult,
}: PriceListsPanelProps) {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [lifecycleTarget, setLifecycleTarget] = useState<PriceListResponse | null>(null);

  const overview = usePriceListsOverview();
  const assortmentQuery = useProducts({});
  const assortmentTotal = assortmentQuery.data?.totalElements ?? null;

  const createMutation = useCreatePriceList();
  const updateMutation = useUpdatePriceList();

  const filteredLists = overview.lists.filter((list) =>
    search.trim() === '' ? true : list.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const summaries: PriceListSummary[] = summarizePriceLists(
    filteredLists,
    overview.bindingsByCode,
    overview.linesByCode,
    overview.linesLoadingCodes,
    assortmentTotal,
  );

  const createButton = (
    <Button
      variant="contained"
      startIcon={<AddRoundedIcon />}
      disabled={!canManage}
      onClick={() => setCreateOpen(true)}
    >
      {t('priceListsPanel.create')}
    </Button>
  );

  const handleCreate = (body: PriceListRequest) => {
    createMutation.mutate(body, {
      onSuccess: (created) => {
        setCreateOpen(false);
        onResult('success', t('priceListsPanel.created'));
        navigate(`/catalog/pricing/price-lists/${encodeURIComponent(created.code)}`);
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  const openLifecycleConfirm = (list: PriceListResponse) => setLifecycleTarget(list);
  const targetSummary = lifecycleTarget
    ? summaries.find((s) => s.list.code === lifecycleTarget.code)
    : undefined;

  const confirmLifecycle = () => {
    if (!lifecycleTarget) return;
    const nextActive = !lifecycleTarget.active;
    updateMutation.mutate(
      { code: lifecycleTarget.code, body: { name: lifecycleTarget.name, kind: lifecycleTarget.kind, active: nextActive } },
      {
        onSuccess: () => {
          setLifecycleTarget(null);
          onResult(
            'success',
            nextActive ? t('priceListsPanel.activated') : t('priceListsPanel.deactivated'),
          );
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_e, next: PriceListsView | null) => {
            if (next) onViewChange(next);
          }}
          aria-label={t('priceListsPanel.viewToggleAria')}
        >
          <ToggleButton value="lists">{t('priceListsPanel.viewLists')}</ToggleButton>
          <ToggleButton value="coverage">{t('priceListsPanel.viewCoverage')}</ToggleButton>
        </ToggleButtonGroup>

        {view === 'lists' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField
              size="small"
              label={t('priceListsPanel.searchLabel')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Tooltip title={t('priceListsPanel.refreshAria')}>
              <IconButton aria-label={t('priceListsPanel.refreshAria')} onClick={() => overview.refetch()}>
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
            {canManage ? (
              createButton
            ) : (
              <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
                <span>{createButton}</span>
              </Tooltip>
            )}
          </Stack>
        ) : null}
      </Stack>

      <Box sx={{ height: 4, mb: 1 }}>
        {overview.isLoading === false && overview.lists.length > 0 && assortmentQuery.isFetching ? (
          <LinearProgress sx={{ height: 4 }} />
        ) : null}
      </Box>

      {view === 'coverage' ? (
        <PriceListCoveragePanel
          lists={overview.lists}
          bindingsByCode={overview.bindingsByCode}
          linesByCode={overview.linesByCode}
          assortmentTotal={assortmentTotal}
          seedStore={seedStore}
          overviewLoading={overview.isLoading}
          overviewError={overview.isError}
          onRetry={overview.refetch}
        />
      ) : overview.isLoading ? (
        <LoadingState label={t('priceListsPanel.loading')} />
      ) : overview.isError ? (
        <ErrorState message={t('priceListsPanel.loadError')} onRetry={overview.refetch} />
      ) : summaries.length === 0 ? (
        search.trim() !== '' ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('priceListsPanel.noMatches')}
          </Typography>
        ) : (
          <EmptyState
            icon={<ReceiptLongRoundedIcon sx={{ fontSize: 48 }} />}
            title={t('priceListsPanel.empty.title')}
            description={t('priceListsPanel.empty.description')}
            action={
              canManage ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateOpen(true)}>
                  {t('priceListsPanel.create')}
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('priceListsPanel.table.name')}</TableCell>
                <TableCell>{t('priceListsPanel.table.code')}</TableCell>
                <TableCell>{t('priceListsPanel.table.kind')}</TableCell>
                <TableCell align="right">{t('priceListsPanel.table.stores')}</TableCell>
                <TableCell>{t('priceListsPanel.table.coverage')}</TableCell>
                <TableCell>{t('priceListsPanel.table.active')}</TableCell>
                <TableCell align="right">{t('priceListsPanel.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaries.map((summary) => {
                const { list } = summary;
                const rowBusy =
                  updateMutation.isPending && updateMutation.variables?.code === list.code;
                const toggle = (
                  <Switch
                    size="small"
                    checked={list.active}
                    disabled={!canAdmin || rowBusy}
                    onChange={() => openLifecycleConfirm(list)}
                    slotProps={{
                      input: { 'aria-label': t('priceListsPanel.activeToggleAria', { name: list.name }) },
                    }}
                  />
                );
                return (
                  <TableRow
                    key={list.code}
                    hover
                    onClick={() => navigate(`/catalog/pricing/price-lists/${encodeURIComponent(list.code)}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {list.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                        {list.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(PRICE_LIST_KIND_LABELS[list.kind] ?? list.kind)}
                        sx={list.kind === 'ADJUSTMENT' ? { opacity: 0.6 } : undefined}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {summary.boundStoresCount === 0 ? (
                        <Tooltip title={t('priceListsPanel.noStoresTooltip')}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            —
                          </Typography>
                        </Tooltip>
                      ) : (
                        summary.boundStoresCount
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                      {summary.linesLoading ? (
                        <CircularProgress size={14} />
                      ) : !summary.linesCount ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2">
                              {summary.coveragePercent ?? '—'}
                              {summary.coveragePercent !== null ? ' %' : ''}
                            </Typography>
                            {summary.nextChangeAt ? (
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={t('priceListsPanel.scheduledChip', {
                                  date: new Intl.DateTimeFormat(undefined, {
                                    day: '2-digit',
                                    month: '2-digit',
                                  }).format(new Date(summary.nextChangeAt)),
                                })}
                              />
                            ) : null}
                          </Stack>
                          {summary.coveragePercent !== null ? (
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, summary.coveragePercent)}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                          ) : null}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canAdmin ? (
                        toggle
                      ) : (
                        <Tooltip title={t('priceListsPanel.noAdminPermissionTooltip')}>
                          <span>{toggle}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={t('priceListsPanel.openAction')}>
                        <IconButton
                          size="small"
                          aria-label={t('priceListsPanel.openActionAria', { name: list.name })}
                          onClick={() =>
                            navigate(`/catalog/pricing/price-lists/${encodeURIComponent(list.code)}`)
                          }
                        >
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* No delete affordance: `DELETE .../price-lists/{code}` does not exist on the
                          shipped BFF contract (only GET/PUT) — the same archive-only gap
                          `PublicationCatalogsPanel` hit for publication catalogs. Deactivation is
                          this list's terminal, merchant-reachable state. */}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PriceListFormDialog
        open={createOpen}
        pending={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />

      <Dialog open={lifecycleTarget !== null} onClose={updateMutation.isPending ? undefined : () => setLifecycleTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {lifecycleTarget?.active
            ? t('priceListsPanel.deactivateConfirmTitle')
            : t('priceListsPanel.activateConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lifecycleTarget?.active
              ? t('priceListsPanel.deactivateConfirmBody', {
                  name: lifecycleTarget?.name,
                  count: targetSummary?.boundStoresCount ?? 0,
                })
              : t('priceListsPanel.activateConfirmBody', {
                  count: targetSummary?.boundStoresCount ?? 0,
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleTarget(null)} disabled={updateMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color={lifecycleTarget?.active ? 'error' : 'primary'}
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={confirmLifecycle}
          >
            {lifecycleTarget?.active ? t('priceListsPanel.deactivateAction') : t('priceListsPanel.activateAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
